import { publishModuleEvent, getSupabaseAccessToken } from '@insolitum/module-events';

import { supabase } from './supabase';

// =============================================================================
// Shell → Living Network event emission.
//
// The shell owns the tenant's ADMINISTRATIVE lifecycle (members, sites, paired
// devices, module subscriptions). Until now none of it reached NATS, so every
// one of those mutations was invisible to NEXUS memory and the Living Network
// while ordinary module activity (LexShift, Provisio, …) was fully observable.
//
// Transport is the same one every embedded module uses:
//   publishModuleEvent → POST {gateway}/api/events/publish
//   → tenant.{org}.events.shell.{event} (Envelope v2) → JetStream → encoder.
// The gateway derives the tenant from the caller's Supabase token, so ONLY
// mutations scoped to the acting user's own organization may be published here
// (see publishShellEvent docs).
//
// NOTE ON THE CATALOG: `packages/types/src/nats.ts` holds a family per producer
// (LEXSHIFT_EVENTS, PROVISIO_EVENTS, …) but has no `shell` family yet, and that
// package is outside this change's scope. The names below deliberately reuse the
// catalog's vocabulary (`*_created` / `*_updated` / `*_removed`, `member_*`
// mirroring `agenda.group_member_*`, `device_*` mirroring `neurosense.*`) so a
// `SHELL_EVENTS` block can be lifted into nats.ts verbatim as the follow-up.
// =============================================================================

/** Producer slug — the `{module}` token of `tenant.{org}.events.{module}.{event}`. */
export const SHELL_EVENT_MODULE = 'shell';

export const SHELL_EVENTS = {
  // Organization membership lifecycle (UsersPage).
  memberInvited: 'member_invited',
  memberRemoved: 'member_removed',
  memberRoleChanged: 'member_role_changed',
  memberBanned: 'member_banned',
  memberUnbanned: 'member_unbanned',

  // Physical footprint of the tenant (SitesPage).
  siteCreated: 'site_created',
  siteUpdated: 'site_updated',
  siteDeleted: 'site_deleted',

  // Paired kiosk / mobile / TV devices (DevicesPage).
  deviceRegistered: 'device_registered',
  deviceUpdated: 'device_updated',
  deviceUnpaired: 'device_unpaired',
  deviceRemoved: 'device_removed',

  // Which organs the tenant runs (ModulesPage).
  moduleSubscriptionStarted: 'module_subscription_started',
  moduleSubscriptionSuspended: 'module_subscription_suspended',
  moduleSubscriptionResumed: 'module_subscription_resumed',
} as const;

export type ShellEvent = (typeof SHELL_EVENTS)[keyof typeof SHELL_EVENTS];

/**
 * Publish a shell event. Fire-and-forget by contract: `publishModuleEvent`
 * swallows every transport failure and returns false, and a missing session is
 * handled below, so this never throws and a failed publish can never roll back
 * or surface on the user's mutation. Call sites `void` it rather than awaiting
 * (as module-lexshift does) so an unreachable gateway cannot even *delay* the
 * post-mutation refresh.
 *
 * Only call this for mutations on the CURRENT organization. The gateway resolves
 * the tenant from the caller's own `user_organizations` row, so a super-admin
 * acting on a foreign tenant would have the event filed under their own org —
 * which is why the super-admin panels deliberately do not emit.
 *
 * Payloads must stay PII-free (ids, roles, statuses, counts) — they are encoded
 * into durable organism memory. Never pass e-mail addresses or licence codes.
 */
export async function publishShellEvent(
  event: ShellEvent,
  data: Record<string, unknown>
): Promise<void> {
  const accessToken = await getSupabaseAccessToken(supabase);
  if (!accessToken) {
    console.warn('[shell-events] No Supabase session, skipping event publish:', event);
    return;
  }

  await publishModuleEvent({
    module: SHELL_EVENT_MODULE,
    event,
    data,
    accessToken,
    // Same gateway the shell's socket already talks to; `resolveEventsUrl`
    // normalizes wss:// → https://. Unset falls back to the package default.
    eventsUrl: import.meta.env.VITE_WS_URL,
  });
}
